import React, { useState } from 'react';
import { Text, Radio, RadioGroup, Stack, Textarea } from '@chakra-ui/react';
import ModalShell from '../../../../../../components/ui/ModalShell';
import { ff } from '../../../../../../theme';

const REASONS = [
  "I'm too busy right now",
  'Not getting the right brand matches',
  'Taking a break',
  'I found another platform',
  'Other',
] as const;

interface DeactivateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the final reason string once the user confirms. */
  onConfirm: (reason: string) => Promise<void>;
}

const DeactivateAccountModal: React.FC<DeactivateAccountModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setReason('');
    setOtherText('');
    onClose();
  };

  const finalReason = reason === 'Other' ? otherText.trim() : reason;
  const canConfirm = Boolean(finalReason) && !isSubmitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    try {
      await onConfirm(finalReason);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Before you go — what's the reason?"
      cancelLabel="Cancel"
      primary={{
        label: 'Deactivate',
        onClick: () => void handleConfirm(),
        tone: 'danger',
        isDisabled: !canConfirm,
        isLoading: isSubmitting,
      }}
    >
      <Text fontSize="15px" lineHeight="1.45" color={ff.body} mb={4}>
        This helps us improve the platform. Your page will be hidden from brands immediately.
      </Text>
      <RadioGroup value={reason} onChange={setReason}>
        <Stack spacing={0} border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden">
          {REASONS.map((r, idx) => (
            <Radio
              key={r}
              value={r}
              minH="52px"
              px={4}
              borderTop={idx === 0 ? 'none' : '1px solid'}
              borderColor={ff.line}
              sx={{
                '.chakra-radio__control': { borderColor: ff.ink },
                '.chakra-radio__control[data-checked]': { bg: ff.ink, borderColor: ff.ink, color: ff.lime },
              }}
            >
              <Text fontSize="15px" color={ff.ink}>{r}</Text>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      {reason === 'Other' && (
        <Textarea
          mt={3}
          placeholder="Tell us more..."
          value={otherText}
          onChange={(e) => setOtherText(e.target.value.slice(0, 300))}
          rows={3}
          resize="none"
          fontSize="15px"
        />
      )}
    </ModalShell>
  );
};

export default DeactivateAccountModal;
